import { Container, IDestroyOptions, Sprite, Text, TextStyle } from "pixi.js";
import { GameFont } from "../../../common/GameFont";
import { DefaultTextStyle } from "./MonBaseContent";
import { gsap } from "gsap";
import { Erc20PaymentProcessingResourcePackage } from "../resources/Erc20PaymentProcessingResourcePackage";
import { PaymentProcessingPopupSceneOptions } from "../scenes/PaymentProcessingPopupSceneOptions";
import { PaymentProcessingType } from "../type/PaymentProcessingType";

export class NotEnoughTokensContainer extends Container {
    private static readonly COPY_TEXT = "Address was copied!";

    private readonly monResources: Erc20PaymentProcessingResourcePackage;
    private readonly options: PaymentProcessingPopupSceneOptions<PaymentProcessingType>;
    private readonly walletAddress: string;

    private notHaveMonText: Text;
    private sendMonText: Text;
    private copyIcon: Sprite;
    private walletAddressText: Text;
    private notificationText: Text;

    public constructor(
        monResources: Erc20PaymentProcessingResourcePackage,
        options: PaymentProcessingPopupSceneOptions<PaymentProcessingType>,
        walletAddress: string
    ) {
        super();
        this.monResources = monResources;
        this.walletAddress = walletAddress;
        this.options = options;
        this.build();
        this.layout();
    }

    private build(): void {
        this.notHaveMonText = new Text(`Looks like you don’t have enough ${this.options.tokenSymbol}\nin your in-game wallet.`, new RedTextStyle());
        this.sendMonText = new Text(`You can send more ${this.options.tokenSymbol} to your\nin-game wallet. Address appears below:`, new DefaultTextStyle());
        this.sendMonText.style.align = "center";
        this.sendMonText.style.letterSpacing = 3;
        this.sendMonText.style.leading = 3.5;
        this.copyIcon = new Sprite(this.monResources.assets_views_erc_20_payment_processing_copy_icon_png);
        this.copyIcon.eventMode = "static";
        this.copyIcon.on("pointerup", this.copyClickHandle, this);
        let address = this.walletAddress;
        if (address.length >= 23) {
            address = `${address.substring(0, 23)}:\n${address.substring(address.length - 8, address.length)}`;
        }
        this.walletAddressText = new Text(address, new AddressTextStyle());
        this.notificationText = new Text(NotEnoughTokensContainer.COPY_TEXT, new TextWithUnderlineTextStyle());
        this.notificationText.alpha = 0;

        this.addChild(
            this.notHaveMonText,
            this.sendMonText,
            this.copyIcon,
            this.walletAddressText,
            this.notificationText
        );
    }

    private async copyClickHandle(): Promise<void> {
        window.FixedClipboard.copy(this.walletAddress);
        const animationTimeLine = new gsap.core.Timeline();
        animationTimeLine.to(this.notificationText, { alpha: 1, duration: 0.3 });
        animationTimeLine.to(this.notificationText, { alpha: 0, duration: 0.3, delay: 1 }, ">");
        await animationTimeLine.play();
        animationTimeLine.kill();
    }

    private layout(): void {
        this.sendMonText.position.set(this.notHaveMonText.width * 0.5 - this.sendMonText.width * 0.5, this.notHaveMonText.y + this.notHaveMonText.height + 5);
        this.copyIcon.position.set(57, this.sendMonText.y + this.sendMonText.height + 60);
        this.walletAddressText.position.set(this.copyIcon.x + this.copyIcon.width + 27, this.copyIcon.y + this.copyIcon.height * 0.5 - this.walletAddressText.height * 0.5 + 5);
        this.notificationText.position.set(170, 360);
    }

    public destroy(options?: boolean | IDestroyOptions): void {
        this.copyIcon.off("pointerup", this.copyClickHandle, this);
        super.destroy(options);
    }
}

class AddressTextStyle extends TextStyle {
    public constructor() {
        super({
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 42,
            fill: '#67EBFF',
            leading: 7.7,
            letterSpacing: 2
        });
    }
}

class TextWithUnderlineTextStyle extends TextStyle {
    public constructor() {
        super({
            fill: "#fefeff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 40
        });
    }
}

export class RedTextStyle extends TextStyle {
    public constructor() {
        super({
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 40,
            fill: ["#FD7B31", "#F61D7C"],
            fillGradientStops: [0.8],
            letterSpacing: 3,
            leading: 3.5,
            align: "center"
        });
    }
}
